import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  IonicModule,
  LoadingController,
  ModalController,
  ToastController
} from '@ionic/angular';
import { ArcElement, Chart, PieController } from 'chart.js';
import { Clipboard } from '@capacitor/clipboard';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Platform } from '@ionic/angular';
import { Observable, Subject, Subscription } from 'rxjs';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import {
  Faktura,
  FakturaDetaljerResponse
} from 'src/app/services/faktura/faktura.types';
import { FeatureTogglesService } from 'src/app/services/feature-toggles/feature-toggles.service';
import { FeatureToggle } from 'src/app/services/feature-toggles/toggles.types';
import { formatDateToString } from 'src/app/utils/date-utils';
import { DataService } from '../../../services/datatransfer/DataService';
import { takeUntil } from 'rxjs/operators';
import { BrukerService } from 'src/app/services/bruker/bruker.service';
import { CommonModule } from '@angular/common';
import { UtsetteBetalingPage } from './utsette-betaling/utsette-betaling.page';

@Component({
  selector: 'app-faktura-popup',
  templateUrl: './faktura-popup.page.html',
  styleUrls: ['./faktura-popup.page.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, UtsetteBetalingPage]
})
export class FakturaPopupPage implements OnInit, OnDestroy {
  @Input() faktura: Faktura;
  @Output() dueDateChanged = new EventEmitter<boolean>();
  @Output() openPdfOutside = new EventEmitter<{
    faktura: Faktura;
    showNettleieId: string;
  }>();
  @ViewChild('mychart', { static: true }) private chartRef;

  fakturaObject: Faktura;
  harRegningForfalt: boolean;
  fakturaStatus: string;

  showDetails = 'hide';
  showNettleie = false;
  nettleieArr: any = [];

  toggleResponse: Observable<FeatureToggle[]>;
  isBetalingsutsettelseToggled: boolean;
  showBetalingsutsettelse = false;
  isOriginalKunde = false;

  formatDateToString = formatDateToString;

  toggleSubscription: Subscription;
  fakturaDetails$ = new Subject();
  pdfView$ = new Subject<void>();
  nettleiePdf$ = new Subject<void>();

  constructor(
    private analyticsService: AnalyticsService,
    public modalCtrl: ModalController,
    public _toast: ToastController,
    private http: HttpClient,
    private dataservice: DataService,
    public loadingController: LoadingController,
    public platform: Platform,
    private toggleService: FeatureTogglesService,
    private brukerService: BrukerService
  ) {
    this.toggleResponse = this.toggleService.getActiveFeatureToggles();
  }
  ngOnDestroy(): void {
    this.toggleSubscription.unsubscribe();
    this.fakturaDetails$.next(undefined);
    this.fakturaDetails$.complete();
    this.pdfView$.next(undefined);
    this.pdfView$.complete();
    this.nettleiePdf$.next(undefined);
    this.nettleiePdf$.complete();
  }

  ngOnInit() {
    Chart.register(PieController, ArcElement);

    this.fakturaObject = this.faktura;
    this.fakturaStatus = this.fakturaObject.status
      .toLowerCase()
      .trim()
      .split(' ')[0];

    const forfall = new Date(this.faktura.endeligForfall);
    this.harRegningForfalt = new Date() > new Date(forfall);

    const apiurl = `https://apiprivat.ishavskraft.no/faktura/detaljer/${this.faktura.fakturanr}`;
    this.http
      .get(apiurl)
      .pipe(takeUntil(this.fakturaDetails$))
      .subscribe((res: FakturaDetaljerResponse) => {
        if (res.fakturaDetaljer) {
          const data = [];
          this.fakturaObject.belopKraft =
            res.fakturaDetaljer.belopKraft.toLocaleString('nb-no');
          this.fakturaObject.belopNettleie =
            res.fakturaDetaljer.belopNettleie.toLocaleString('nb-no');
          this.fakturaObject.belopTotal =
            res.fakturaDetaljer.faktura.belop.toLocaleString('nb-no');

          if (res.fakturaDetaljer.hasGn) {
            this.showNettleie = true;
            this.nettleieArr = res.fakturaDetaljer.documents;
          }

          data.push(res.fakturaDetaljer.belopKraft);
          data.push(res.fakturaDetaljer.belopNettleie);
          const labels = ['Kraft', 'Nettleie'];
          const chart = new Chart(this.chartRef.nativeElement, {
            type: 'pie',
            data: {
              labels: labels,
              datasets: [
                {
                  data: data,
                  backgroundColor: ['#002987', '#0a66ff'],
                  hoverBackgroundColor: ['#002987', '#0a66ff']
                }
              ]
            },
            options: {
              maintainAspectRatio: true,
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  enabled: false
                }
              }
            }
          });
          this.showDetails = 'show';
        }
      });
    this.toggleSubscription = this.toggleResponse.subscribe((res) => {
      this.isBetalingsutsettelseToggled =
        this.getIsBetalingsutsettelseToggled(res) ?? false;
    });
    this.brukerService.getBrukerkunde().subscribe({
      next: (res) => {
        if (!res?.inUse?.tildelt) {
          this.isOriginalKunde = true;
        } else {
          this.isOriginalKunde = false;
        }
      }
    });
  }

  public closeModal(showFakturaWeb: boolean) {
    this.dataservice.setShowFaktura(
      showFakturaWeb,
      this.faktura.fakturanr.toString(),
      false
    );
    this.modalCtrl.dismiss();
  }

  public closeModalGjennomfakt(showFakturaWeb: boolean, fakturanr: string) {
    this.dataservice.setShowFaktura(showFakturaWeb, fakturanr, true);
    this.modalCtrl.dismiss();
  }

  getIsBetalingsutsettelseToggled(toggles: FeatureToggle[]): boolean {
    const myToggle = toggles.find((x) => x.navn === 'uc_betalingsutsettelse');
    return myToggle ? myToggle.toggleActive === 'NOW' : false;
  }

  public async openBetalingsutsettelse() {
    this.showBetalingsutsettelse = true;
    this.analyticsService.logEvent_selectContent(
      'regninger åpne betalingsutsettelse'
    );
  }

  async getPdf() {
    const fakturanr = this.faktura.fakturanr;
    const apiurl = `https://apiprivat.ishavskraft.no/faktura/pdf/view/${fakturanr}`;

    if (this.platform.is('capacitor')) {
      const loading = await this.loadingController.create({
        message: 'Laster faktura...',
        mode: this.platform.is('android') ? 'md' : 'ios'
      });
      await loading.present();
      let headers = new HttpHeaders();
      headers = headers.set('Accept', 'application/pdf');

      this.http
        .get(apiurl, { headers: headers, responseType: 'blob' })
        .pipe(takeUntil(this.pdfView$))
        .subscribe((res: Blob) => {
          const reader = new FileReader();
          reader.readAsDataURL(res);
          reader.onloadend = () => {
            const base64data = reader.result;
            const filename = `Faktura${fakturanr}.pdf`;
            this.analyticsService.logEvent_selectContent(
              'regninger laste ned pdf'
            );
            try {
              this.openPdf(base64data, filename, loading).catch((err) => {
                // console.log('Feil i åpne pdf ', JSON.stringify(err));
              });
            } catch (e) {
              // console.log(e);
              // TODO: skal vi håndtere dette?
            }
          };
        });
    } else {
      // Platform er nettleser e.l.
      this.openPdfOutside.emit({ faktura: this.faktura, showNettleieId: '' });
      this.modalCtrl.dismiss();
    }
  }

  async openPdf(data: any, filename: string, load: HTMLIonLoadingElement) {
    try {
      await Filesystem.writeFile({
        path: filename,
        data: data,
        directory: Directory.Cache
      }).then((writeRes) => {
        FileOpener.open({
          filePath: writeRes.uri
        });
        load.dismiss();
      });
    } catch (err) {
      load.dismiss();
      return Promise.reject();
    }
  }

  async getNettleiePdf() {
    if (this.showNettleie && this.nettleieArr && this.nettleieArr.length > 0) {
      if (this.platform.is('capacitor')) {
        const loading = await this.loadingController.create({
          message: 'Laster faktura...',
          mode: this.platform.is('android') ? 'md' : 'ios'
        });
        await loading.present();

        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/pdf');
        const apiurl = `https://apiprivat.ishavskraft.no/faktura/gjennomfakt/pdf/view/${this.nettleieArr[0]}`;

        this.http
          .get(apiurl, { headers: headers, responseType: 'blob' })
          .pipe(takeUntil(this.nettleiePdf$))
          .subscribe((res: any) => {
            const filename = `Nettleie${this.nettleieArr[0]}.pdf`;

            this.analyticsService.logEvent_selectContent(
              'regninger laste ned nettleie pdf'
            );
            try {
              this.openPdf(res, filename, loading).catch((err) => {
                //
              });
            } catch (e) {
              // console.log(e);
              // TODO: må dette håndteres?
            }
          });
      } else {
        this.openPdfOutside.emit({
          faktura: this.faktura,
          showNettleieId: this.nettleieArr[0]
        });
        this.modalCtrl.dismiss();
      }
    }
  }

  copyToClipboard(text: string) {
    if (text == null || text.length == 0) {
      return;
    }

    if (this.platform.is('capacitor')) {
      Clipboard.write({ string: text }).then(
        () => {
          this._toast
            .create({
              message:
                text == '15038799999' ? 'Kontonummer kopiert' : 'KID kopiert',
              duration: 2500,
              position: 'middle'
              //color:'success'
            })
            .then((toast) => {
              toast.present();
              if (text == '15038799999')
                this.analyticsService.logEvent_selectContent(
                  'regninger kontonr kopiert'
                );
              else
                this.analyticsService.logEvent_selectContent(
                  'regninger kidnr kopiert'
                );
            });
        },
        function (e) {
          // TODO: feilhåndtering?
        }
      );
    } else {
      navigator.clipboard.writeText(text).then(
        () => {
          this._toast
            .create({
              message:
                text == '15038799999' ? 'Kontonummer kopiert' : 'KID kopiert',
              duration: 2500,
              position: 'middle'
              //color:'success'
            })
            .then((toast) => {
              toast.present();
              if (text == '15038799999')
                this.analyticsService.logEvent_selectContent(
                  'regninger kontonr kopiert'
                );
              else
                this.analyticsService.logEvent_selectContent(
                  'regninger kidnr kopiert'
                );
            });
        },
        function (e) {
          // TODO: feilhåndtering?
        }
      );
    }
  }

  emitDueDateChanged() {
    this.dueDateChanged.emit(true);
  }

  dismissUtsettelse() {
    this.showBetalingsutsettelse = false;
  }
}
